import { useLazyQuery } from '@apollo/client/react';
import { useEffect, useMemo, useState } from 'react';
import { Checkbox } from '@mui/material';
import { generateColumnsDefaultConfiguration } from '../../../../components/dataGrid/columnHelpers';
import { GET_TIPO_NOTIFICACIONES } from '../../../../data/graphQL/queries/AdminRoles/getNotificaciones';
import { GET_NOTIFICACIONES_ROL } from '../../../../data/graphQL/queries/AdminRoles/getNotificacionesRol';
import { IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';

export default function useRoleNotifications({ roleId, onError }) {
  const [catalogRows, setCatalogRows] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [draftRows, setDraftRows] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [getTipoNotificaciones, { data: dataNotificacion, error: errorNotificacion, loading: loadingNotificacion }] = useLazyQuery(GET_TIPO_NOTIFICACIONES, {
    fetchPolicy: 'cache-and-network'
  });

  const [getNotificacionRol, { data: dataNotificacionRol, error: errorNotificacionRol, loading: loadingNotificacionRol }] = useLazyQuery(GET_NOTIFICACIONES_ROL, {
    fetchPolicy: 'cache-and-network'
  });

  useEffect(() => {
    getTipoNotificaciones();
  }, []);

  useEffect(() => {
    if (!dataNotificacion?.notificacionesTipo?.success) return;
    setCatalogRows(dataNotificacion.notificacionesTipo.valorEsperado || []);
  }, [dataNotificacion]);

  useEffect(() => {
    if (errorNotificacion) onError?.('ERRORDB');
  }, [errorNotificacion, onError]);

  useEffect(() => {
    if (!roleId || roleId <= 0 || catalogRows.length === 0) return;
    getNotificacionRol({ variables: { idRol: roleId } });
  }, [roleId, catalogRows, getNotificacionRol]);

  useEffect(() => {
    if (!dataNotificacionRol?.notificacionRol?.success) return;
    const rows = dataNotificacionRol.notificacionRol.valorEsperado || [];
    setSelectedRows(rows);
    setDraftRows(rows);
  }, [dataNotificacionRol]);

  useEffect(() => {
    if (errorNotificacionRol) onError?.('ERRORDB');
  }, [errorNotificacionRol, onError]);

  const selectedIds = useMemo(
    () => new Set(selectedRows.map((row) => row.idNotificacionTipo)),
    [selectedRows]
  );

  const availableRows = useMemo(
    () => catalogRows.filter((row) => !selectedIds.has(row.idNotificacionTipo)),
    [catalogRows, selectedIds]
  );

  const getCatalogTitle = (id) => {
    const row = catalogRows.find((item) => item.idNotificacionTipo === id);
    return row?.titulo || '';
  };

  const getDraftById = (id) => draftRows.find((row) => row.idNotificacionTipo === id);

  const toggleNotification = (row) => {
    const item = getDraftById(row.idNotificacionTipo);
    if (item) {
      setDraftRows((current) => current.filter((entry) => entry.idNotificacionTipo !== row.idNotificacionTipo));
      return;
    }

    setDraftRows((current) => [...current, { idNotificacionTipo: row.idNotificacionTipo, obligatorio: false }]);
  };

  const toggleObligatorio = (row) => {
    const item = getDraftById(row.idNotificacionTipo);
    if (!item) {
      setDraftRows((current) => [...current, { idNotificacionTipo: row.idNotificacionTipo, obligatorio: true }]);
      return;
    }

    setDraftRows((current) => current.map((entry) => (
      entry.idNotificacionTipo === row.idNotificacionTipo
        ? { ...entry, obligatorio: !item.obligatorio }
        : entry
    )));
  };

  const removeSelected = (row) => {
    const filtered = selectedRows.filter((item) => item.idNotificacionTipo !== row.idNotificacionTipo);
    setSelectedRows(filtered);
    setDraftRows(filtered);
  };

  const openDialog = () => {
    setDraftRows(selectedRows);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDraftRows(selectedRows);
    setDialogOpen(false);
  };

  const confirmDialog = () => {
    setSelectedRows(draftRows);
    setDialogOpen(false);
  };

  const selectedColumns = useMemo(() => generateColumnsDefaultConfiguration([
    { field: 'idNotificacionTipo', headerName: 'ID', valueGetter: (_, row) => row.idNotificacionTipo },
    { field: 'titulo', headerName: 'Titulo', valueGetter: (_, row) => getCatalogTitle(row.idNotificacionTipo) },
    { field: 'obligatorio', type: 'boolean', headerName: 'Obligatorio' },
    {
      field: 'accionesNotificacion',
      headerName: 'Acciones',
      flex: 0.08,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title='Eliminar'>
          <IconButton onClick={() => removeSelected(params.row)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      )
    }
  ]), [catalogRows, selectedRows]);

  const availableColumns = useMemo(() => generateColumnsDefaultConfiguration([
    {
      field: 'seleccionar',
      headerName: 'Seleccionar',
      flex: 0.05,
      sortable: false,
      renderCell: (params) => (
        <Checkbox
          checked={Boolean(getDraftById(params.row.idNotificacionTipo))}
          onChange={() => toggleNotification(params.row)}
        />
      )
    },
    { field: 'idNotificacionTipo', flex: 0.1, headerName: 'ID' },
    { field: 'titulo', headerName: 'Titulo' },
    { field: 'condicionDisparador', headerName: 'Cond. Disparador' },
    { field: 'descripcion', headerName: 'Descripcion' },
    {
      field: 'obligatorioToggle',
      headerName: 'Obligatorio',
      flex: 0.1,
      sortable: false,
      renderCell: (params) => (
        <Checkbox
          checked={Boolean(getDraftById(params.row.idNotificacionTipo)?.obligatorio)}
          onChange={() => toggleObligatorio(params.row)}
        />
      )
    }
  ]), [draftRows]);

  return {
    selectedRows,
    availableRows,
    dialogOpen,
    selectedColumns,
    availableColumns,
    openDialog,
    closeDialog,
    confirmDialog,
    removeSelected,
    loading: loadingNotificacion || loadingNotificacionRol,
    error: errorNotificacion || errorNotificacionRol
  };
}
